/**
 * AWS Configuration for FallVision Frontend
 *
 * Configuration Loading Strategy:
 * 1. Production/QA/Dev: Values injected at runtime via window.APP_CONFIG (from SSM via start.sh)
 * 2. Local Development: Values from environment variables (REACT_APP_* in .env file)
 *
 * SECURITY: No hardcoded credentials - all values come from SSM (cloud) or .env file (local)
 *
 * IMPORTANT - WHY THIS FILE WAS CHANGED (fix for blank page on deployment):
 * The previous version used a getRequiredConfig() function that threw an Error if
 * window.APP_CONFIG was missing. This caused a blank page on the deployed site because:
 *   1. index.html loads env-config.js (which sets window.APP_CONFIG) via a <script> tag
 *   2. During deployment, install_dependencies.sh wipes /var/www/html and copies the new
 *      React build - but the new build does NOT include env-config.js (it is generated
 *      at runtime by start.sh, which runs AFTER install_dependencies.sh)
 *   3. So between install_dependencies.sh and start.sh, env-config.js does not exist
 *   4. The browser gets a 404 for env-config.js, so window.APP_CONFIG is never set
 *   5. The old throw crashed the entire JS bundle before React could render anything
 *      resulting in a completely blank page with no login screen
 *
 * FIX: getRequiredConfig() now returns null instead of throwing. buildAwsConfig() returns
 * null if any value is missing, and index.js handles that null gracefully so React still
 * renders. The user will see a proper error state instead of a blank page.
 */

/**
 * Safely reads a config value.
 * Checks window.APP_CONFIG first (set by env-config.js on cloud deployments),
 * then falls back to REACT_APP_* environment variables (used in local development).
 * Returns null instead of throwing if neither source has the value.
 */
const getRequiredConfig = (runtimeValue, envVarName) => {
  if (runtimeValue) return runtimeValue;
  return process.env[envVarName] || null;
};

/**
 * Builds and returns the full Amplify config object.
 * Returns null if any required value is missing so the caller can handle
 * it gracefully without crashing the JS bundle.
 */
export const buildAwsConfig = () => {
  const userPoolId = getRequiredConfig(window.APP_CONFIG?.cognitoUserPoolId, 'REACT_APP_USER_POOL_ID');
  const userPoolClientId = getRequiredConfig(window.APP_CONFIG?.cognitoClientId, 'REACT_APP_CLIENT_ID');
  const identityPoolId = getRequiredConfig(window.APP_CONFIG?.identityPoolId, 'REACT_APP_IDENTITY_POOL_ID');
  const bucket = getRequiredConfig(window.APP_CONFIG?.dashboardPhotosBucket, 'REACT_APP_S3_BUCKET');

  if (!userPoolId || !userPoolClientId || !identityPoolId || !bucket) {
    // Cloud: check that start.sh ran successfully and generated env-config.js
    // Local: check that frontend/.env file exists with REACT_APP_* values
    console.error('AWS configuration incomplete. Check env-config.js was generated by start.sh (cloud) or .env file (local).');
    return null;
  }

  return {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        identityPoolId,
        region: 'us-east-1',
        loginWith: { email: true }
      }
    },
    Storage: {
      S3: { bucket, region: 'us-east-1' }
    }
  };
};

// awsConfig is kept for any files that import it directly.
// Prefer using buildAwsConfig() where possible so null can be handled explicitly.
export const awsConfig = buildAwsConfig();
